/**
 * O disparo do WhatsApp no ticket (spec 014/025), nas DUAS visões — a
 * completa (aba Informações gerais) e a compacta do atendimento:
 *
 * - `TicketDispatchBlock`: o que aconteceu, com a hora de cada passo — Na fila
 *   → Disparado → Entregue → Respondeu, ou o desfecho (Falhou, Fora da fila,
 *   Adiado) com o motivo traduzido —, se foi à mão e por qual número. Pedido do
 *   piloto em 01/10: "mostrar que foi entregue … no próprio ticket";
 * - `useTicketDispatchMenu`: "Disparar agora" e "Tirar da fila de disparo" no
 *   menu de ações, com a confirmação. A regra é UMA (dispatchActionsFor, do
 *   @erp/shared), a mutação é uma (useTicketDispatchAction) e o texto é um
 *   (DISPATCH_COPY) — as duas visões chamam este hook, nenhuma repete a
 *   condição.
 */
import { useState, type ComponentType, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCheck,
  CircleX,
  Clock,
  Hand,
  Hourglass,
  ListX,
  LoaderCircle,
  Reply,
  Send,
  Smartphone,
} from 'lucide-react';
import {
  DISPATCH_STEP_LABELS,
  TICKET_DISPATCH_STATUS_LABELS,
  dispatchActionsFor,
  dispatchReasonLabel,
  dispatchReasonOf,
  dispatchSteps,
  whatsappChannelLabel,
  type DispatchStep,
  type DispatchStepKind,
  type TicketDetail,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Badge, Dialog } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { ticketDispatchQuery, useTicketDispatchAction } from './dispatch-queries';
import { DISPATCH_STATUS_VARIANT } from './ticket-dispatch-badge';
import { errorMessage, isTicketMirrored } from './ticket-format';

/** spec 014: as duas ações manuais do disparo, com o que cada uma faz dito antes */
export const DISPATCH_COPY = {
  dispatch: {
    title: 'Disparar agora',
    body: (n: number) =>
      `O modelo de WhatsApp da mesa será enviado agora ao contato do ticket #${n}, fora da fila e do horário do disparo — mas contando na cota do dia. O ticket vai para o estágio de envio.`,
    cta: 'Disparar agora',
  },
  remove: {
    title: 'Tirar da fila de disparo',
    body: (n: number) =>
      `O ticket #${n} sai da fila e não recebe o disparo automático. Ainda dá para disparar à mão depois.`,
    cta: 'Tirar da fila',
  },
} as const;

type DispatchMenuAction = keyof typeof DISPATCH_COPY;

/**
 * Os itens do menu (`items`, para dentro do DropdownMenuContent) e o diálogo de
 * confirmação (`dialog`, fora dele). Sem regra ligada, ticket fechado ou
 * espelhado, `items` é vazio.
 */
export function useTicketDispatchMenu(
  ticket: Pick<TicketDetail, 'ticketNumber' | 'status' | 'mirrored'>
): { items: ReactNode; dialog: ReactNode } {
  const q = useQuery(ticketDispatchQuery(ticket.ticketNumber));
  const action = useTicketDispatchAction(ticket.ticketNumber);
  const [confirm, setConfirm] = useState<DispatchMenuAction | null>(null);

  const actions = dispatchActionsFor({
    dispatch: q.data?.dispatch ?? null,
    // sem a resposta ainda, nenhuma ação: "regra ligada" é o que a API diz
    ruleEnabled: q.data?.ruleEnabled === true,
    ticketClosed: ticket.status.kind === 'closed' || ticket.status.kind === 'canceled',
    ticketMirrored: isTicketMirrored(ticket),
  });
  const open = (a: DispatchMenuAction) => {
    action.reset();
    setConfirm(a);
  };

  const items = (
    <>
      {actions.canDispatch ? (
        <DropdownMenuItem onSelect={() => open('dispatch')}>
          <Send />
          Disparar agora
        </DropdownMenuItem>
      ) : null}
      {actions.canRemove ? (
        <DropdownMenuItem onSelect={() => open('remove')}>
          <ListX />
          Tirar da fila de disparo
        </DropdownMenuItem>
      ) : null}
    </>
  );

  const copy = confirm ? DISPATCH_COPY[confirm] : null;
  const dialog =
    confirm && copy ? (
      <Dialog open onClose={() => setConfirm(null)} title={copy.title}>
        <p className="text-sm text-muted-foreground">{copy.body(ticket.ticketNumber)}</p>
        {action.isError ? (
          <p className="mt-2 text-sm text-destructive">{errorMessage(action.error)}</p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirm(null)}>
            Voltar
          </Button>
          <Button
            size="sm"
            disabled={action.isPending}
            onClick={() => action.mutate(confirm, { onSuccess: () => setConfirm(null) })}
          >
            {copy.cta}
          </Button>
        </div>
      </Dialog>
    ) : null;

  return { items, dialog };
}

// ── o bloco ─────────────────────────────────────────────────────────────────

const STEP_ICON: Record<DispatchStepKind, ComponentType<{ className?: string }>> = {
  enqueued: Clock,
  sending: LoaderCircle,
  sent: Send,
  delivered: CheckCheck,
  replied: Reply,
  failed: CircleX,
  skipped: ListX,
  postponed: Hourglass,
  waiting: Hourglass,
};

const STEP_TONE: Record<DispatchStepKind, string> = {
  enqueued: 'text-muted-foreground',
  sending: 'text-[#1677FF]',
  sent: 'text-[#1677FF]',
  delivered: 'text-[#1677FF]',
  replied: 'text-[#52C41A]',
  failed: 'text-destructive',
  skipped: 'text-muted-foreground',
  postponed: 'text-[#D48806]',
  waiting: 'text-[#D48806]',
};

const pad = (n: number) => String(n).padStart(2, '0');

/** "06:56" no dia de hoje; "30/09 06:56" nos outros — a hora exata fica no title */
export function dispatchTime(at: Date | string, now: Date = new Date()): string {
  const d = new Date(at);
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return d.toDateString() === now.toDateString()
    ? hm
    : `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${hm}`;
}

const fullTime = (at: Date | string) => new Date(at).toLocaleString('pt-BR');

/** o rótulo de um passo; o adiado diz ATÉ quando espera */
function StepItem({ step, first }: { step: DispatchStep; first: boolean }) {
  const Icon = STEP_ICON[step.kind];
  const waits = step.kind === 'postponed' || step.kind === 'waiting';
  return (
    <li
      className="flex items-center gap-1.5 whitespace-nowrap"
      title={step.at ? fullTime(step.at) : undefined}
    >
      {first ? null : (
        <span aria-hidden className="text-muted-foreground/60">
          →
        </span>
      )}
      <Icon
        className={cn(
          'size-3.5 shrink-0',
          STEP_TONE[step.kind],
          step.kind === 'sending' && 'animate-spin'
        )}
      />
      <span className={cn('font-medium', STEP_TONE[step.kind])}>
        {DISPATCH_STEP_LABELS[step.kind]}
      </span>
      {step.at ? (
        <span className="text-muted-foreground tabular-nums">
          {waits ? `até ${dispatchTime(step.at)}` : dispatchTime(step.at)}
        </span>
      ) : null}
    </li>
  );
}

/**
 * O disparo do ticket: some quando o ticket nunca entrou na fila. Mostra também
 * o que saiu da fila sem disparar (o selo esconde; aqui é o motivo que importa).
 */
export function TicketDispatchBlock({
  ticketNumber,
  className,
}: {
  ticketNumber: number;
  className?: string;
}) {
  const q = useQuery(ticketDispatchQuery(ticketNumber));
  const terms = useTerms();
  const dispatch = q.data?.dispatch ?? null;
  if (!dispatch) return null;

  const steps = dispatchSteps(dispatch);
  // sem telefone, diz onde o disparo procurou (a mesma lista que o envio percorre)
  const reason = dispatchReasonLabel(dispatchReasonOf(dispatch), terms, q.data?.phoneSources);
  const channel = q.data?.channel ?? null;
  // adiado antes de sair (o "Disparar agora" zera a conta)
  const postponedBefore =
    dispatch.status !== 'pending' && dispatch.postponeCount > 0 ? dispatch.postponeCount : 0;

  return (
    <section
      aria-label="Disparo do WhatsApp"
      className={cn('rounded-lg border bg-card p-4 text-card-foreground shadow-sm', className)}
    >
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <Send className="size-3.5 text-muted-foreground" aria-hidden />
        <h2 className="text-sm font-semibold">Disparo do WhatsApp</h2>
        <Badge variant={DISPATCH_STATUS_VARIANT[dispatch.status]} className="ml-auto">
          {TICKET_DISPATCH_STATUS_LABELS[dispatch.status]}
        </Badge>
      </header>

      <ol
        aria-label="Passos do disparo"
        className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs"
      >
        {steps.map((step, i) => (
          <StepItem key={step.kind} step={step} first={i === 0} />
        ))}
      </ol>

      {reason ? (
        <p
          className={cn(
            'mt-2 text-xs',
            dispatch.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {reason}
        </p>
      ) : null}

      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Hand className="size-3" aria-hidden />
          {dispatch.manual ? 'Manual — “Disparar agora”' : 'Automático — fila da mesa'}
        </span>
        {channel ? (
          <span
            className="inline-flex min-w-0 items-center gap-1"
            title={`Número da empresa: ${whatsappChannelLabel(channel)}`}
          >
            <Smartphone className="size-3 shrink-0" aria-hidden />
            <span className="truncate">{whatsappChannelLabel(channel, { short: true })}</span>
          </span>
        ) : null}
        {postponedBefore > 0 ? (
          <span>
            Adiado {postponedBefore} {postponedBefore === 1 ? 'vez' : 'vezes'} antes de sair
          </span>
        ) : null}
      </p>
    </section>
  );
}
